// src/components/UploadsManager.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./styles/MasterSite.css";
import { AuthContext } from "../context/AuthContext";

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp)$/i;

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function UploadsManager() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const isSuperAdmin = user?.role === "super-admin";

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const isBrowser = typeof window !== "undefined";
  const isLocalhost = isBrowser && window.location?.hostname === "localhost";
  const apiBase =
    process.env.REACT_APP_SERVER_URL || (isLocalhost ? "http://localhost:5000" : "");
  const makeFullUrl = (rawUrl) => {
    if (!rawUrl) return "#";
    if (!isLocalhost) return `/${rawUrl.replace(/^\/+/, "")}`;
    const base = (apiBase || "").replace(/\/$/, "");
    return `${base}${rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`}`;
  };

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [query, setQuery] = useState("");
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [selected, setSelected] = useState({});
  const [busyFile, setBusyFile] = useState(null);

  const fetchFiles = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const res = await axios.get("/api/uploads", authHeader);
      setFiles(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErrMsg(e?.response?.data?.message || e.message || "Failed to load uploads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return files.filter((f) => {
      if (q && !f.filename.toLowerCase().includes(q)) return false;
      if (onlyUnused && (f.usedInMaintenance > 0 || f.usedInCashbook > 0)) return false;
      return true;
    });
  }, [files, query, onlyUnused]);

  const totalSize = useMemo(
    () => files.reduce((sum, f) => sum + (f.sizeBytes || 0), 0),
    [files]
  );
  const unusedSize = useMemo(
    () =>
      files
        .filter((f) => !f.usedInMaintenance && !f.usedInCashbook)
        .reduce((sum, f) => sum + (f.sizeBytes || 0), 0),
    [files]
  );

  const deleteFile = async (filename, { force = false } = {}) => {
    setBusyFile(filename);
    setErrMsg("");
    try {
      await axios.delete(
        `/api/uploads/${encodeURIComponent(filename)}${force ? "?force=true" : ""}`,
        authHeader
      );
      setFiles((prev) => prev.filter((f) => f.filename !== filename));
      setSelected((prev) => {
        const next = { ...prev };
        delete next[filename];
        return next;
      });
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 409) {
        const usedIn = [];
        if (data?.usedInMaintenance) usedIn.push(`${data.usedInMaintenance} maintenance log(s)`);
        if (data?.usedInCashbook) usedIn.push(`${data.usedInCashbook} cashbook entry(ies)`);
        const ok = window.confirm(
          `"${filename}" is still linked to ${usedIn.join(" and ")}.\n\n` +
            `Deleting it anyway will remove the photo/receipt from those records too.\n\n` +
            `Delete anyway?`
        );
        if (ok) {
          await deleteFile(filename, { force: true });
          return;
        }
      } else {
        setErrMsg(data?.message || e.message || "Delete failed");
      }
    } finally {
      setBusyFile(null);
    }
  };

  const handleDeleteClick = (f) => {
    const inUse = f.usedInMaintenance > 0 || f.usedInCashbook > 0;
    if (!inUse) {
      if (window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) {
        deleteFile(f.filename);
      }
      return;
    }
    // in-use file: deleteFile will surface the 409 confirm flow
    deleteFile(f.filename);
  };

  const toggleSelected = (filename) =>
    setSelected((prev) => ({ ...prev, [filename]: !prev[filename] }));

  const selectedUnusedFilenames = useMemo(
    () =>
      filtered
        .filter((f) => selected[f.filename] && !f.usedInMaintenance && !f.usedInCashbook)
        .map((f) => f.filename),
    [filtered, selected]
  );

  const deleteSelected = async () => {
    if (!selectedUnusedFilenames.length) return;
    if (
      !window.confirm(
        `Delete ${selectedUnusedFilenames.length} unused file(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    for (const filename of selectedUnusedFilenames) {
      // eslint-disable-next-line no-await-in-loop
      await deleteFile(filename);
    }
  };

  if (!isAdmin) {
    return (
      <div className="master-site-container">
        <p>Access denied. Only admins can manage uploaded files.</p>
      </div>
    );
  }

  return (
    <div className="master-site-container">
      <div className="ms-topbar">
        <div className="ms-head">
          <div className="ms-title">Uploaded Files</div>
          <div className="ms-subtitle">
            Manage site photos and cashbook receipts stored on the server to free up space.
          </div>
        </div>

        <div className="ms-stats">
          <div className="ms-stat">
            <div className="ms-stat-label">Files</div>
            <div className="ms-stat-value">{files.length}</div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-label">Total Size</div>
            <div className="ms-stat-value">{formatBytes(totalSize)}</div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-label">Unused (safe to delete)</div>
            <div className="ms-stat-value">{formatBytes(unusedSize)}</div>
          </div>
        </div>
      </div>

      <div className="ms-controls">
        <div className="ms-search">
          <input
            className="input-control"
            placeholder="Search filename…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={onlyUnused}
            onChange={(e) => setOnlyUnused(e.target.checked)}
          />
          Show only unused files
        </label>

        <button
          type="button"
          className="ms-mini-btn"
          onClick={fetchFiles}
          disabled={loading}
        >
          Refresh
        </button>

        {isSuperAdmin && (
          <button
            type="button"
            className="ms-mini-btn"
            onClick={deleteSelected}
            disabled={!selectedUnusedFilenames.length}
          >
            Delete Selected ({selectedUnusedFilenames.length})
          </button>
        )}
      </div>

      {errMsg && <div className="ms-error">⚠️ {errMsg}</div>}

      <table className="report-table">
        <thead>
          <tr>
            {isSuperAdmin && <th></th>}
            <th>Preview</th>
            <th>Filename</th>
            <th>Size</th>
            <th>Modified</th>
            <th>Used In</th>
            {isSuperAdmin && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan="7">
                <div className="ms-loading">Loading files…</div>
              </td>
            </tr>
          )}

          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan="7">
                <div className="ms-empty">No files found.</div>
              </td>
            </tr>
          )}

          {!loading &&
            filtered.map((f) => {
              const inUse = f.usedInMaintenance > 0 || f.usedInCashbook > 0;
              return (
                <tr key={f.filename}>
                  {isSuperAdmin && (
                    <td>
                      {!inUse && (
                        <input
                          type="checkbox"
                          checked={!!selected[f.filename]}
                          onChange={() => toggleSelected(f.filename)}
                        />
                      )}
                    </td>
                  )}
                  <td>
                    {IMAGE_EXT.test(f.filename) ? (
                      <a href={makeFullUrl(f.url)} target="_blank" rel="noopener noreferrer">
                        <img
                          src={makeFullUrl(f.url)}
                          alt=""
                          style={{ height: 48, width: 48, objectFit: "cover", borderRadius: 4 }}
                        />
                      </a>
                    ) : (
                      <a href={makeFullUrl(f.url)} target="_blank" rel="noopener noreferrer">
                        File
                      </a>
                    )}
                  </td>
                  <td style={{ wordBreak: "break-all" }}>{f.filename}</td>
                  <td>{formatBytes(f.sizeBytes)}</td>
                  <td className="nowrap">
                    {f.modifiedAt ? new Date(f.modifiedAt).toLocaleString() : "-"}
                  </td>
                  <td>
                    {inUse ? (
                      <span className="ms-chip">
                        {f.usedInMaintenance > 0 ? `${f.usedInMaintenance} maintenance` : ""}
                        {f.usedInMaintenance > 0 && f.usedInCashbook > 0 ? " · " : ""}
                        {f.usedInCashbook > 0 ? `${f.usedInCashbook} cashbook` : ""}
                      </span>
                    ) : (
                      <span className="ms-chip">unused</span>
                    )}
                  </td>
                  {isSuperAdmin && (
                    <td>
                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => handleDeleteClick(f)}
                        disabled={busyFile === f.filename}
                      >
                        {busyFile === f.filename ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
